import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton
} from '@mui/material';
import {
  QrCodeScanner as QRIcon,
  Inventory as StorageIcon,
  CallReceived as ReceiveIcon,
  CallMade as SendIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import { getApiUrl } from '../utils/apiUrl';

const MobileQuickScan = () => {
  const [scanMode, setScanMode] = useState(null); // 'order', 'location', null
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Order data
  const [scannedOrder, setScannedOrder] = useState(null);
  const [userTeam, setUserTeam] = useState(null);
  const [storageStatus, setStorageStatus] = useState(null);
  
  // Action selection
  const [selectedAction, setSelectedAction] = useState(null); // 'store', 'retrieve'
  const [quantity, setQuantity] = useState(1);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showTeamSelectDialog, setShowTeamSelectDialog] = useState(false);
  const [availableTeams, setAvailableTeams] = useState([]);

  const getToken = () => localStorage.getItem('token');
  const API_BASE = getApiUrl();

  // Request permissions (same as MobileQRScanner)
  const requestPermissions = async () => {
    const { camera } = await BarcodeScanner.requestPermissions();
    return camera === 'granted' || camera === 'limited';
  };

  // Start QR scanning (same approach as MobileQRScanner)
  const startScan = async (mode, action = null) => {
    try {
      setError('');
      setScanMode(mode);
      
      // Set action if provided
      if (action) {
        setSelectedAction(action);
      }

      // Check if BarcodeScanner is available
      if (!BarcodeScanner) {
        setError('Barcode scanner er ikke tilgjengelig på denne enheten');
        console.error('BarcodeScanner plugin not available');
        return;
      }

      // Request permissions
      console.log('Requesting camera permissions...');
      const granted = await requestPermissions();
      console.log('Permission granted:', granted);
      
      if (!granted) {
        setError('Kamera-tillatelse er nødvendig for å skanne QR-koder. Gå til Innstillinger > Apper > Produksjonsstyring > Tillatelser og aktiver Kamera.');
        return;
      }

      setScanning(true);

      // Start scanning with timeout (30 seconds)
      console.log('Starting camera scan...');
      
      const scanPromise = BarcodeScanner.scan();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Scan timeout')), 30000)
      );

      let result;
      try {
        result = await Promise.race([scanPromise, timeoutPromise]);
      } catch (timeoutErr) {
        console.log('Scan timed out or cancelled');
        try {
          await BarcodeScanner.stopScan();
        } catch (e) {
          console.log('Stop scan error:', e);
        }
        setScanning(false);
        setError('Skanning avbrutt. Trykk på tilbake-knappen på enheten for å avbryte skanning.');
        return;
      }

      const { barcodes } = result;
      console.log('Scan completed, barcodes found:', barcodes?.length || 0);

      setScanning(false);

      if (barcodes && barcodes.length > 0) {
        const qrData = barcodes[0].rawValue;
        console.log('QR Code scanned:', qrData);
        
        if (mode === 'order') {
          await handleOrderScanned(qrData);
        } else if (mode === 'location') {
          // Pass action parameter to handleLocationScanned
          await handleLocationScanned(qrData, action);
        }
      } else {
        setError('Ingen QR-kode funnet');
      }
    } catch (err) {
      console.error('Scan error:', err);
      console.error('Error details:', JSON.stringify(err));
      setScanning(false);
      
      // User cancelled with back button
      if (err.message && err.message.includes('cancelled')) {
        console.log('Scan cancelled by user');
        return;
      }
      
      // More specific error messages
      if (err.message && err.message.includes('permission')) {
        setError('Kamera-tilgang nektet. Vennligst aktiver kamera-tillatelse i enhetens innstillinger.');
      } else if (err.message && err.message.includes('camera')) {
        setError('Kunne ikke starte kameraet. Sjekk at ingen andre apper bruker kameraet.');
      } else {
        setError(`Kunne ikke skanne QR-kode: ${err.message || 'Ukjent feil'}`);
      }
    }
  };

  // Stop scanning (called manually if user cancels)
  const stopScan = async () => {
    try {
      await BarcodeScanner.stopScan();
    } catch (e) {
      console.log('Stop scan error:', e);
    }
    setScanning(false);
    setScanMode(null);
    setSelectedAction(null);
  };

  // Handle order QR scanned
  const handleOrderScanned = async (qrContent) => {
    setLoading(true);
    setError('');
    
    try {
      // Use the SAME endpoint as MobileQRScanner
      const orderResponse = await fetch(
        `${API_BASE}/work-orders/search-qr/${encodeURIComponent(qrContent)}`,
        {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }
      );

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        throw new Error(errorData.message || 'Ordre ikke funnet');
      }

      const order = await orderResponse.json();
      setScannedOrder(order);

      // Get user's teams (same as MobileQRScanner)
      const teamsResponse = await fetch(`${API_BASE}/teams/my-teams`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const userTeams = await teamsResponse.json();

      // Find ALL user's teams on this order
      const myTeams = order.teams?.filter(t => 
        userTeams.some(ut => ut.id === t.id)
      ) || [];

      if (myTeams.length === 0) {
        setError('Du er ikke tildelt denne ordren');
        setScannedOrder(null);
        setLoading(false);
        return;
      }

      // If user is on multiple teams, let them choose
      if (myTeams.length > 1) {
        setAvailableTeams(myTeams);
        setShowTeamSelectDialog(true);
        setLoading(false);
        return;
      }

      // Single team - proceed
      setUserTeam(myTeams[0]);

      // Get storage status
      const storageResponse = await fetch(`${API_BASE}/storage-transactions/work-order/${order.id}/status`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const storage = await storageResponse.json();
      setStorageStatus(storage);

      // Show action selection
      setShowActionDialog(true);
      
    } catch (err) {
      console.error('Order scan error:', err);
      setError(err.message || 'Kunne ikke laste ordre');
      setScannedOrder(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle location QR scanned
  const handleLocationScanned = async (qrContent, action) => {
    // Use parameter action if provided, otherwise fall back to state
    const actionToUse = action || selectedAction;
    
    if (!scannedOrder || !userTeam || !actionToUse) {
      setError('Noe gikk galt. Vennligst start på nytt.');
      console.error('Missing data:', { scannedOrder: !!scannedOrder, userTeam: !!userTeam, action: actionToUse });
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Parse location - should be location ID or number
      const locationId = qrContent;
      console.log('Looking for location:', locationId);

      // Verify location exists
      const locationResponse = await fetch(`${API_BASE}/storage-locations`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!locationResponse.ok) {
        console.error('Failed to fetch locations:', locationResponse.status);
        throw new Error('Kunne ikke hente lagringsplasser');
      }
      
      const locations = await locationResponse.json();
      console.log('Available locations:', locations);
      
      const location = locations.find(l => 
        l.id === parseInt(locationId) || 
        l.location_number === locationId ||
        l.location_number === locationId.padStart(3, '0') ||
        String(l.id) === locationId
      );
      
      console.log('Found location:', location);

      if (!location) {
        throw new Error(`Lagringsplass "${locationId}" ikke funnet`);
      }

      if (actionToUse === 'store') {
        // Store parts (check_in)
        const storeResponse = await fetch(`${API_BASE}/storage-transactions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            workOrderId: scannedOrder.id,
            teamId: userTeam.id,
            storageLocationId: location.id,
            action: 'check_in',
            quantity: quantity,
            notes: `Lagret via mobil QR-skanning`
          })
        });

        if (!storeResponse.ok) {
          const errorData = await storeResponse.json();
          throw new Error(errorData.error || 'Kunne ikke lagre');
        }

        setSuccess(`✓ Lagret ${quantity} stk på ${location.location_number}!`);
        
      } else if (actionToUse === 'retrieve') {
        // Retrieve parts (check_out)
        const previousTeam = getPreviousTeam();
        if (!previousTeam) {
          throw new Error('Ingen forrige team funnet');
        }

        const retrieveResponse = await fetch(`${API_BASE}/storage-transactions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            workOrderId: scannedOrder.id,
            teamId: previousTeam.id,
            storageLocationId: location.id,
            action: 'check_out',
            quantity: quantity,
            notes: `Hentet av ${userTeam.name} via mobil`
          })
        });

        if (!retrieveResponse.ok) {
          const errorData = await retrieveResponse.json();
          throw new Error(errorData.error || 'Kunne ikke hente');
        }

        setSuccess(`✓ Hentet ${quantity} stk fra ${location.location_number}!`);
      }

      // Reset after success
      setTimeout(() => {
        resetScan();
      }, 3000);

    } catch (err) {
      console.error('Location scan error:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      setError(err.message || 'Kunne ikke lagre/hente. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  // Get previous team in sequence
  const getPreviousTeam = () => {
    if (!scannedOrder || !userTeam) return null;
    
    const teams = scannedOrder.teams || [];
    const currentIndex = teams.findIndex(t => t.id === userTeam.id);
    
    if (currentIndex > 0) {
      return teams[currentIndex - 1];
    }
    return null;
  };

  // Check if user team has parts in storage
  const teamHasPartsInStorage = () => {
    if (!storageStatus || !userTeam) return false;
    
    // Ensure storageStatus is an array
    const statusArray = Array.isArray(storageStatus) ? storageStatus : [];
    
    return statusArray.some(s => 
      s.stored_by_teams?.some(t => 
        t.name?.toLowerCase().trim() === userTeam.name?.toLowerCase().trim()
      )
    );
  };

  // Check if previous team has parts
  const previousTeamHasPartsInStorage = () => {
    const previousTeam = getPreviousTeam();
    if (!previousTeam || !storageStatus) return false;
    
    // Ensure storageStatus is an array
    const statusArray = Array.isArray(storageStatus) ? storageStatus : [];
    
    return statusArray.some(s => 
      s.stored_by_teams?.some(t => 
        t.name?.toLowerCase().trim() === previousTeam.name?.toLowerCase().trim()
      )
    );
  };

  // Get suggested action
  const getSuggestedAction = () => {
    const hasStorage = teamHasPartsInStorage();
    const previousHasStorage = previousTeamHasPartsInStorage();
    const isFirstTeam = !getPreviousTeam();

    if (hasStorage) {
      return null; // Already stored, no action
    }
    
    if (previousHasStorage) {
      return 'retrieve'; // Previous team has parts, should retrieve
    }
    
    if (isFirstTeam || !previousHasStorage) {
      return 'store'; // First team or previous done, should store
    }

    return 'store'; // Default to store
  };

  // Handle team selection (when user is on multiple teams)
  const handleTeamSelect = async (team) => {
    setShowTeamSelectDialog(false);
    setUserTeam(team);
    setLoading(true);

    try {
      // Get storage status
      const storageResponse = await fetch(`${API_BASE}/storage-transactions/work-order/${scannedOrder.id}/status`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const storage = await storageResponse.json();
      setStorageStatus(storage);

      // Show action selection
      setShowActionDialog(true);
    } catch (err) {
      console.error('Error loading storage status:', err);
      setError('Kunne ikke laste lagringsstatus');
    } finally {
      setLoading(false);
    }
  };

  // Reset scan
  const resetScan = () => {
    setScannedOrder(null);
    setUserTeam(null);
    setStorageStatus(null);
    setSelectedAction(null);
    setQuantity(1);
    setShowActionDialog(false);
    setShowDetailsDialog(false);
    setShowTeamSelectDialog(false);
    setAvailableTeams([]);
    setScanMode(null);
    setSuccess('');
    setError('');
  };

  // Cleanup on unmount (not needed with direct scan approach)
  useEffect(() => {
    return () => {
      // Scan cleanup is automatic with direct scan() approach
    };
  }, []);

  // Render action dialog
  const renderActionDialog = () => {
    const suggestedAction = getSuggestedAction();
    const previousTeam = getPreviousTeam();
    const hasStorage = teamHasPartsInStorage();

    return (
      <Dialog open={showActionDialog} onClose={() => setShowActionDialog(false)} fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Ordre #{scannedOrder?.orderNumber}</Typography>
            <IconButton onClick={() => setShowActionDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {scannedOrder?.customer_name}
            </Typography>
            <Chip 
              label={`Ditt team: ${userTeam?.name}`} 
              color="primary" 
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {hasStorage && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Deler allerede lagret! Ingen handling nødvendig.
            </Alert>
          )}

          {!hasStorage && (
            <>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Hva vil du gjøre?
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                <Button
                  fullWidth
                  variant={suggestedAction === 'store' ? 'contained' : 'outlined'}
                  color="primary"
                  size="large"
                  startIcon={<SendIcon />}
                  onClick={() => {
                    setShowActionDialog(false);
                    startScan('location', 'store');
                  }}
                  disabled={hasStorage}
                >
                  📦 Lagre deler
                  {suggestedAction === 'store' && ' (Anbefalt)'}
                </Button>

                {previousTeam && (
                  <Button
                    fullWidth
                    variant={suggestedAction === 'retrieve' ? 'contained' : 'outlined'}
                    color="secondary"
                    size="large"
                    startIcon={<ReceiveIcon />}
                    onClick={() => {
                      setShowActionDialog(false);
                      startScan('location', 'retrieve');
                    }}
                    disabled={!previousTeamHasPartsInStorage()}
                  >
                    ⬆️ Hent deler fra {previousTeam.name}
                    {suggestedAction === 'retrieve' && ' (Anbefalt)'}
                  </Button>
                )}

                <Button
                  fullWidth
                  variant="text"
                  startIcon={<InfoIcon />}
                  onClick={() => {
                    setShowActionDialog(false);
                    setShowDetailsDialog(true);
                  }}
                >
                  Se detaljer
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { resetScan(); }}>
            Avbryt
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Render details dialog
  const renderDetailsDialog = () => {
    return (
      <Dialog open={showDetailsDialog} onClose={() => setShowDetailsDialog(false)} fullWidth>
        <DialogTitle>
          Ordre #{scannedOrder?.orderNumber}
        </DialogTitle>
        <DialogContent>
          <List>
            {scannedOrder?.teams?.map((team, index) => {
              const isUserTeam = team.id === userTeam?.id;
              const statusArray = Array.isArray(storageStatus) ? storageStatus : [];
              const teamHasStorage = statusArray.some(s =>
                s.stored_by_teams?.some(t => t.name === team.name)
              );

              return (
                <ListItem key={team.id} sx={{ bgcolor: isUserTeam ? 'primary.light' : 'transparent', borderRadius: 1 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={isUserTeam ? 'bold' : 'normal'}>
                          {index + 1}. {team.name}
                        </Typography>
                        {isUserTeam && <Chip label="Ditt team" size="small" color="primary" />}
                      </Box>
                    }
                    secondary={
                      <>
                        <Chip 
                          label={team.status_name || 'Venter'} 
                          size="small" 
                          sx={{ mt: 0.5 }}
                        />
                        {teamHasStorage && (
                          <Chip 
                            label="📦 Lagret" 
                            size="small" 
                            color="success"
                            sx={{ mt: 0.5, ml: 0.5 }}
                          />
                        )}
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowDetailsDialog(false);
            setShowActionDialog(true);
          }}>
            Tilbake
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Render team selection dialog
  const renderTeamSelectDialog = () => {
    return (
      <Dialog open={showTeamSelectDialog} onClose={() => setShowTeamSelectDialog(false)} fullWidth>
        <DialogTitle>
          Velg Team
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Du er på flere teams for denne ordren. Velg hvilket team du jobber for nå:
          </Typography>
          
          <List>
            {availableTeams.map((team, index) => {
              // Check if this team has stored parts
              const statusArray = Array.isArray(storageStatus) ? storageStatus : [];
              const teamHasStorage = statusArray.some(s => 
                s.stored_by_teams?.some(t => t.name?.toLowerCase().trim() === team.name?.toLowerCase().trim())
              );

              return (
                <ListItem 
                  key={team.id} 
                  button
                  onClick={() => handleTeamSelect(team)}
                  sx={{ 
                    mb: 1, 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography fontWeight="bold">
                        {index + 1}. {team.name}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Chip 
                          label={team.status_name || 'Ingen status'} 
                          size="small" 
                          sx={{ mr: 0.5 }}
                        />
                        {teamHasStorage && (
                          <Chip 
                            label="📦 Lagret" 
                            size="small" 
                            color="success"
                          />
                        )}
                        {!teamHasStorage && index > 0 && (
                          <Chip 
                            label="⏳ Må hente" 
                            size="small" 
                            color="warning"
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowTeamSelectDialog(false);
            resetScan();
          }}>
            Avbryt
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Main render
  return (
    <Box sx={{ p: 2 }}>
      {/* Success message */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
        {error}
      </Alert>
    )}

    {/* Scanning state */}
    {scanning && (
      <Card sx={{ mb: 2, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent sx={{ textAlign: 'center' }}>
          <QRIcon sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Skanner...
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {scanMode === 'order' ? 'Skann QR-kode på ordre' : 'Skann QR-kode for lagringsplass'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 2, opacity: 0.9 }}>
            💡 Trykk på enhetens tilbake-knapp for å avbryte
          </Typography>
          <Button variant="contained" color="error" onClick={stopScan}>
            Avbryt
          </Button>
        </CardContent>
      </Card>
    )}

    {/* Loading state */}
    {loading && (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    )}

    {/* Main action selection - CHECK IN / CHECK OUT */}
    {!scanning && !loading && !scannedOrder && !selectedAction && (
      <Card sx={{ textAlign: 'center' }}>
        <CardContent sx={{ py: 4 }}>
          <StorageIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Produksjonslager
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Velg hva du vil gjøre
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="large"
              startIcon={<SendIcon />}
              onClick={() => {
                setSelectedAction('store');
                startScan('order');
              }}
              sx={{ py: 3, fontSize: '1.2rem', fontWeight: 'bold' }}
            >
              📦 Sett på Reol
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              1️⃣ Skann Ordre QR → 2️⃣ Skann Reol QR
            </Typography>

            <Button
              fullWidth
              variant="contained"
              color="warning"
              size="large"
              startIcon={<ReceiveIcon />}
              onClick={() => {
                setSelectedAction('retrieve');
                startScan('order');
              }}
              sx={{ py: 3, fontSize: '1.2rem', fontWeight: 'bold' }}
            >
              ⬆️ Ta ut av Reol
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              1️⃣ Skann Ordre QR → 2️⃣ Skann Reol QR
            </Typography>
          </Box>
        </CardContent>
      </Card>
    )}

    {/* Order scanned - ready to scan location */}
    {!scanning && !loading && scannedOrder && selectedAction && (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Ordre #{scannedOrder.orderNumber}
            </Typography>
            <Chip 
              label={selectedAction === 'store' ? '📦 Sett på Reol' : '⬆️ Ta ut av Reol'} 
              color={selectedAction === 'store' ? 'success' : 'warning'}
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {scannedOrder.customer_name}
          </Typography>
          
          {userTeam && (
            <Chip 
              label={`Team: ${userTeam.name}`} 
              color="primary" 
              size="small"
              sx={{ mt: 1, mb: 2 }}
            />
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            Skann lagringsplass
          </Typography>
          
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<QRIcon />}
            onClick={() => startScan('location', selectedAction)}
            sx={{ py: 2, mt: 1 }}
          >
            📷 Skann Lagringsplass QR
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            onClick={resetScan}
            sx={{ mt: 2 }}
          >
            Avbryt
          </Button>
        </CardContent>
      </Card>
    )}

      {/* Dialogs */}
      {renderTeamSelectDialog()}
      {renderActionDialog()}
      {renderDetailsDialog()}
    </Box>
  );
};

export default MobileQuickScan;
